<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BillingListItem',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
    },
    templateParams: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getContainer = (...args) => {
    return {
      self: $fn.value.urlFor('create_billing_list_item'),
      object: 'list',
      data: [],
    };
  };
  const getPayload = (...args) => {
    const [editor] = args;
    const exclude = [];

    return editor.isNew
      ? editor.getData({ exclude })
      : editor.getChanges({ exclude, skipEmbeddedLists: true });
  };

  const formId = ref(null);
  const formName = ref('billingListItem');
  const messages = ref({
    createTitle: $fn.value.pgettext('BillingListItem', 'New billing list item'),
  });
  const resource = ref(null);
  const editor = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    formName: formName.value,
    messages: messages.value,
    resource: resource.value,
    editor: editor.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_resource = () => {
    return props.oid
      ? $fn.value.fetch('read_billing_list_item', {
          parameters: { id: props.oid },
          asResource: true,
        })
      : $fn.value.fetch('get_billing_list_item_template', {
          parameters: props.templateParams,
          asResource: true,
        });
  };
  const $$load_editor = () => {
    return resource.value.editor();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'formName' },
      { name: 'messages' },
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      formId: $$load_formId,
      resource: $$load_resource,
      editor: $$load_editor,
    },
    ux: {},
    dataRefs: {
      formId,
      formName,
      messages,
      resource,
      editor,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isDeletable = computed(() => {
    return props.oid != null;
  });
  const editTitle = computed(() => {
    if (loaded.value) {
      return resource.value.description;
    }
    return null;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: resource.value.isNew
          ? messages.value.createTitle
          : messages.value.editTitle,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.templateParams,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('templateParams');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_kc30g2bn3_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6466705138206980_reloadGrid_jr6r2obj6_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'billingListItem',
    };
  };
  const act_1040167445267876_emit_fhxfqb9uh_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data,
    };
  };
  const act_1040167445267876_reloadGrid_64aogsa57_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'billingList',
    };
  };
  const act_1040167445267876_rest_sapn5amhx_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: getContainer(editor.value),
      resource: editor.value,
      data: getPayload(editor.value),
    };
  };
  const act_5534025912102772_emit_7z2ywfeij_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data,
    };
  };

  const act_7315092382398562_closeModal_kc30g2bn3 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_kc30g2bn3_getActionArgs,
    });
  const inst_evh_7315092382398562_close_p6qmjkky7 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_kc30g2bn3,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_p6qmjkky7],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_28hsyz5qr =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_2k9kb0tuo =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_28hsyz5qr,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_2k9kb0tuo],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_hcg1gkwxw =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_02wfgo6kj =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_hcg1gkwxw,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_02wfgo6kj],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_lv2g0hdz1 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_al1i7u0hc =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_lv2g0hdz1,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_al1i7u0hc],
    event: 'reload',
  });
  const act_6466705138206980_closeComponent_50lu55pzm =
    new actionTypes.CloseComponentAction({});
  const inst_evh_6466705138206980_deleted_0d40xhy90 =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeComponent_50lu55pzm,
      event: 'deleted',
      displayName: 'closeComponent',
    });
  const act_6466705138206980_reloadGrid_jr6r2obj6 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_6466705138206980_reloadGrid_jr6r2obj6_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_4qqirrbwv =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadGrid_jr6r2obj6,
      event: 'deleted',
      displayName: 'reloadGrid',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_6466705138206980_deleted_0d40xhy90,
      inst_evh_6466705138206980_deleted_4qqirrbwv,
    ],
    event: 'deleted',
  });
  const act_1040167445267876_emit_fhxfqb9uh = new actionTypes.EmitAction({
    actionArgs: act_1040167445267876_emit_fhxfqb9uh_getActionArgs,
  });
  const inst_evh_1040167445267876_saved_bxvyw0gbc =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_emit_fhxfqb9uh,
      event: 'saved',
      displayName: 'emit',
    });
  const act_1040167445267876_reloadGrid_64aogsa57 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1040167445267876_reloadGrid_64aogsa57_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_0y0rdkdlm =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadGrid_64aogsa57,
      event: 'saved',
      displayName: 'reloadGrid',
    });
  const act_1040167445267876_rest_sapn5amhx = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_sapn5amhx_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_1040167445267876_saved_bxvyw0gbc,
      inst_evh_1040167445267876_saved_0y0rdkdlm,
    ],
  });
  const inst_evh_1040167445267876_submit_2hqkipt4a =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_sapn5amhx,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_2hqkipt4a],
    event: 'submit',
  });
  const act_5534025912102772_reloadComponentData_ol24b978n =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5534025912102772_reload_h20pso0bi =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_reloadComponentData_ol24b978n,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5534025912102772_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_reload_h20pso0bi],
    event: 'reload',
  });
  const act_5534025912102772_closeComponent_iegne3b2v =
    new actionTypes.CloseComponentAction({});
  const inst_evh_5534025912102772_close_feeeizyvp =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_closeComponent_iegne3b2v,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_5534025912102772_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_close_feeeizyvp],
    event: 'close',
  });
  const act_5534025912102772_emit_7z2ywfeij = new actionTypes.EmitAction({
    actionArgs: act_5534025912102772_emit_7z2ywfeij_getActionArgs,
  });
  const inst_evh_5534025912102772_changed_wosbjqcv8 =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_emit_7z2ywfeij,
      event: 'changed',
      displayName: 'emit',
    });
  const inst_evh_5534025912102772_changed = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_changed_wosbjqcv8],
    event: 'changed',
  });
  const act_2806493579509315_closeComponent_zvwmx1ros =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2806493579509315_clickPreventStop_9whot7eoo =
    new actionTypes.EventHandlerImpl({
      action: act_2806493579509315_closeComponent_zvwmx1ros,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2806493579509315_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2806493579509315_clickPreventStop_9whot7eoo],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_p6qmjkky7,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_2k9kb0tuo,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_02wfgo6kj,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_reload_al1i7u0hc,
    inst_evh_6466705138206980_reload,
    inst_evh_6466705138206980_deleted_0d40xhy90,
    inst_evh_6466705138206980_deleted_4qqirrbwv,
    inst_evh_6466705138206980_deleted,
    inst_evh_1040167445267876_saved_bxvyw0gbc,
    inst_evh_1040167445267876_saved_0y0rdkdlm,
    inst_evh_1040167445267876_submit_2hqkipt4a,
    save: inst_evh_1040167445267876_submit_2hqkipt4a,
    inst_evh_1040167445267876_submit,
    inst_evh_5534025912102772_reload_h20pso0bi,
    inst_evh_5534025912102772_reload,
    inst_evh_5534025912102772_close_feeeizyvp,
    inst_evh_5534025912102772_close,
    inst_evh_5534025912102772_changed_wosbjqcv8,
    inst_evh_5534025912102772_changed,
    inst_evh_2806493579509315_clickPreventStop_9whot7eoo,
    inst_evh_2806493579509315_clickPreventStop,
  };

  const evh_7315092382398562_close_p6qmjkky7 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_p6qmjkky7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_2k9kb0tuo = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_2k9kb0tuo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_02wfgo6kj = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_02wfgo6kj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_al1i7u0hc = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_al1i7u0hc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_0d40xhy90 = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_0d40xhy90.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_4qqirrbwv = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_4qqirrbwv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_bxvyw0gbc = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_bxvyw0gbc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_0y0rdkdlm = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_0y0rdkdlm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_2hqkipt4a = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_2hqkipt4a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_reload_h20pso0bi = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_reload_h20pso0bi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_reload = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_close_feeeizyvp = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_close_feeeizyvp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_close = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_changed_wosbjqcv8 = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_changed_wosbjqcv8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_changed = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2806493579509315_clickPreventStop_9whot7eoo = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2806493579509315_clickPreventStop_9whot7eoo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2806493579509315_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2806493579509315_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="BillingListItem"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.billingList.color"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <UcBillingListItemHeader
          :item="resource"
          @reload="evh_6466705138206980_reload($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
        >
        </UcBillingListItemHeader>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        :readonly="resource.isReadOnly"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetBillingListItem
          :item="editor"
          :form="forms[formName]"
          @reload="evh_5534025912102772_reload($event, {})"
          @close="evh_5534025912102772_close($event, {})"
          @changed="evh_5534025912102772_changed($event, {})"
        >
        </FieldsetBillingListItem>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter
        :card="!modal"
        :modal="modal"
        :color="$config.register.color"
        v-if="loaded"
      >
        <ItpBox :gap="2" class="ms-auto">
          <ItpButton
            :form="forms[formName]"
            :text="pgettext('BillingListItem/Button', 'Create item')"
            type="submit"
            :spinning="actions.save.isRunning"
            v-if="resource.isNew"
          >
          </ItpButton>
          <ItpButton
            :form="formId"
            :text="pgettext('Button', 'Save changes')"
            type="submit"
            :spinning="actions.save.isRunning"
            v-if="!resource.isNew && !resource.isReadOnly && forms[formName]"
          >
          </ItpButton>
          <ItpButton
            :text="gettext('Close')"
            variant="default"
            @click.prevent.stop="
              evh_2806493579509315_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </UcFooter>
    </template>
  </ItpPage>
</template>
